import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import get from 'lodash.get';
import {
    withStyles,
    Grid,
    Typography,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    IconButton,
    Menu,
    MenuItem,
    StyleRulesCallback,
} from '@material-ui/core';
import CancelPresentation from '@material-ui/icons/CancelPresentation';

import {
    fetchLanguageList,
    updateDetailSettings,
    exportTranslations,
    exportAddOnTranslations,
    importTranslations,
    importAddOnTranslations,
    fetchInstalledListAddonsRequest,
} from 'Console/data/applicationDetail/settings/SettingsAction';

import {
    selectApplicationMetaDataLanguage,
    selectLanguageListData,
    selectLanguageListIsLoading,
    selectApplicationLocalization,
    selectLanguageAddOnsInstalledList,
    selectApplicationMetaDataLanguageUsedLocales,
    selectAppId,
} from 'Console/data/applicationDetail/settings/SettingsSelector';

import FormInput from 'Console/components/common/form/FormInput';
import FormSelect from 'Console/components/common/form/FormSelect';

import {
    LOCALIZATION_SOURCE_OPTIONS,
    LANGUAGE_READ,
    DEFAULT_LANGUAGE_READ_FROM,
} from 'Console/constants/appConstants';

const styles: StyleRulesCallback = (theme) => ({
    root: {
        flexGrow: 1,
        alignItems: 'center',
        padding: '0 24px',
        fontSize: theme.typography.pxToRem(14),
    },
    title: {
        display: 'inline-block',
        fontWeight: 'bold',
        paddingBottom: theme.typography.pxToRem(20),
    },
    message: {
        fontSize: theme.typography.pxToRem(14),
        paddingBottom: theme.typography.pxToRem(34),
    },
    btn: {
        marginBottom: theme.typography.pxToRem(65),
    },
    btnAdd: {
        marginTop: theme.spacing.unit + 7,
        marginLeft: theme.spacing.unit * 5 + 7,
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
        },
    },
    btnImport: {
        marginRight: theme.typography.pxToRem(15),
    },
    listItems: {
        paddingTop: theme.spacing.unit * 2 + 4,
        paddingBottom: theme.spacing.unit * 2 + 4,
    },
    listItem: {
        padding: 0,
    },
    boxList: {
        width: '45%',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    languageItems: {
        [theme.breakpoints.down('lg')]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    smallText: {
        fontSize: theme.typography.pxToRem(14),
        paddingRight: theme.typography.pxToRem(24),
        paddingLeft: theme.typography.pxToRem(24),
        [theme.breakpoints.down('lg')]: {
            paddingTop: theme.typography.pxToRem(24),
            paddingBottom: theme.typography.pxToRem(8),
            paddingLeft: 0,
            paddingRight: 0,
        },
    },
    importTranslationsInput: {
        display: 'none',
    },
    formInput: {
        width: '33%',
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
        },
    },
    formInputLarge: {
        width: '100%',
        [theme.breakpoints.between('xs', 'sm')]: {
            width: '100%',
        },
    },
});

const ITEM_HEIGHT = 48;

const Language = ({
    classes,
    intl,
    form,
    form: { getFieldValue, getFieldDecorator, setFieldsValue },
}): React.ReactElement => {
    const [anchorElExport, setAnchorElExport] = useState<(EventTarget & HTMLElement) | null>(null);
    const [anchorElImport, setAnchorElImport] = useState<(EventTarget & HTMLElement) | null>(null);

    const dispatch = useDispatch();

    const appId = useSelector(selectAppId);
    const languageMetaData = useSelector(selectApplicationMetaDataLanguage);
    const languageList = useSelector(selectLanguageListData);
    const languageListIsLoading = useSelector(selectLanguageListIsLoading);
    const localization = useSelector(selectApplicationLocalization);
    const addonsWithLanguage = useSelector(selectLanguageAddOnsInstalledList);
    const usedLocales = useSelector(selectApplicationMetaDataLanguageUsedLocales);

    useEffect(() => {
        dispatch(fetchLanguageList(appId));
        dispatch(fetchInstalledListAddonsRequest(appId));
    }, [dispatch, appId]);

    const handleAddLanguage = useCallback(() => {
        const { locale, usedLocales } = getFieldValue('language');

        if (!locale) {
            return;
        }

        setFieldsValue({
            'language.usedLocales': [...usedLocales, { code: locale.id, label: locale.name }],
            'language.locale': null,
        });
    }, [getFieldValue, setFieldsValue]);

    const handleRemoveLanguage = useCallback(
        (localeToRemove) => {
            const { usedLocales } = getFieldValue('language');

            setFieldsValue({
                'language.usedLocales': usedLocales.filter(
                    (locale) => locale.code !== localeToRemove.code
                ),
            });
        },
        [getFieldValue, setFieldsValue]
    );

    const handleAddOnExportTranslations = (addon) => () => {
        dispatch(
            exportAddOnTranslations(addon, {
                success: intl.formatMessage({ id: 'Notification.export.success' }),
                fail: intl.formatMessage({ id: 'Notification.export.fail' }),
            })
        );
        setAnchorElExport(null);
    };

    const handleExportTranslations = (format) => () => {
        dispatch(
            exportTranslations(
                {
                    success: intl.formatMessage({ id: 'Notification.export.success' }),
                    fail: intl.formatMessage({ id: 'Notification.export.fail' }),
                },
                format
            )
        );
        setAnchorElExport(null);
    };

    const handleAddOnImportTranslations = (addon) => (event) => {
        const file = event.target.files[0];
        setAnchorElImport(null);
        dispatch(
            importAddOnTranslations(addon, file, {
                success: intl.formatMessage({ id: 'Notification.import.success' }),
                fail: intl.formatMessage({ id: 'Notification.import.fail' }),
            })
        );
    };

    const handleImportTranslations = (event) => {
        const file = event.target.files[0];
        setAnchorElImport(null);
        dispatch(
            importTranslations(file, {
                success: intl.formatMessage({ id: 'Notification.import.success' }),
                fail: intl.formatMessage({ id: 'Notification.import.fail' }),
            })
        );
    };

    const handleSaveSettings = useCallback(() => {
        const { usedLocales, localizationSource, path, readLanguageFrom, fallback } = getFieldValue(
            'language'
        );

        const fallbackLanguageValue = fallback ? fallback.id : '';

        dispatch(
            updateDetailSettings(
                {
                    'metadata.language.usedLocales': usedLocales,
                    'localization.sourceType': localizationSource ? localizationSource.id : '',
                    'localization.valuePath': path || '',
                    'metadata.language.readFrom': readLanguageFrom.id,
                    'metadata.language.fallbackLanguage.value': fallbackLanguageValue,
                },
                {
                    success: intl.formatMessage({ id: 'Notification.apply' }),
                    fail: intl.formatMessage({ id: 'Notification.fail' }),
                }
            )
        );
    }, [dispatch, getFieldValue, intl]);

    getFieldDecorator('language.usedLocales', {
        initialValue:
            languageMetaData && languageMetaData.usedLocales
                ? languageMetaData && languageMetaData.usedLocales
                : [],
        preserve: true,
    });

    const languageFallbackOptions = getFieldValue('language.usedLocales').map((locale) => ({
        id: locale.code,
        name: locale.label,
    }));

    const fallbackLanguage = usedLocales.find(
        (locale) => locale.code === get(languageMetaData, 'fallbackLanguage.value')
    );

    const initialFallbackLanguage = fallbackLanguage
        ? { id: fallbackLanguage.code, name: fallbackLanguage.label }
        : {};

    const COOKIE = 'cookie';
    const USER_PROVIDED = 'userProvided';

    const isLocalizationSourceSelectedValueCookie =
        get(localization, 'sourceType', '') === COOKIE ||
        getFieldValue('language.localizationSource')?.id === COOKIE;

    const isLocalizationSourceSelectedValueUserProvided =
        getFieldValue('language.localizationSource')?.id === USER_PROVIDED;

    return (
        <Grid container className={classes.root}>
            <Grid item xs={12}>
                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="Settings.language.applicationLocalization" />
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage id="Settings.language.message" />
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="Settings.language.languages" />
                    </Typography>
                </Grid>

                {/* @TODO - create autocomplete selection  https://material-ui.com/demos/autocomplete/#react-autosuggest */}
                {!languageListIsLoading && languageList && languageList.length > 0 ? (
                    <Grid container direction="row" alignItems="flex-end">
                        <Grid className={classes.formInput}>
                            <FormSelect
                                form={form}
                                fullWidth
                                isRequired
                                formItemKey="language.locale"
                                label={intl.formatMessage({ id: 'Settings.language.label' })}
                                optionList={languageList.map((language) => ({
                                    id: language.code,
                                    name: language.names.en,
                                }))}
                                optionListIsLoading={languageListIsLoading}
                            />
                        </Grid>

                        <Typography>
                            <Button
                                id="yeself-button"
                                variant="contained"
                                color="primary"
                                onClick={handleAddLanguage}
                                className={classes.btnAdd}
                            >
                                <FormattedMessage id="Buttons.add" />
                            </Button>
                        </Typography>
                    </Grid>
                ) : null}

                <Grid item className={classes.boxList}>
                    <List className={classes.listItems}>
                        {getFieldValue('language.usedLocales') &&
                            getFieldValue('language.usedLocales').map((locale) => (
                                <ListItem className={classes.listItem} key={locale.code}>
                                    <ListItemText primary={locale.label} />
                                    <ListItemIcon>
                                        <IconButton
                                            aria-label="Delete"
                                            onClick={() => handleRemoveLanguage(locale)}
                                            disabled={
                                                locale.code === getFieldValue('language.fallback')?.id
                                            }
                                        >
                                            <CancelPresentation />
                                        </IconButton>
                                    </ListItemIcon>
                                </ListItem>
                            ))}
                    </List>
                </Grid>

                <Grid item style={{ paddingBottom: '2.125rem' }} className={classes.formInput}>
                    <FormSelect
                        form={form}
                        fullWidth
                        isRequired
                        label={intl.formatMessage({ id: 'Settings.language.readFrom' })}
                        formItemKey="language.readLanguageFrom"
                        initialValue={LANGUAGE_READ.find(
                            (option) =>
                                option.id ===
                                get(languageMetaData, 'readFrom', DEFAULT_LANGUAGE_READ_FROM)
                        )}
                        optionList={LANGUAGE_READ}
                    />
                </Grid>

                <Grid item style={{ paddingBottom: '2.125rem' }} className={classes.formInput}>
                    <FormSelect
                        form={form}
                        fullWidth
                        isRequired
                        label={intl.formatMessage({ id: 'Settings.language.fallback' })}
                        formItemKey="language.fallback"
                        optionList={languageFallbackOptions}
                        initialValue={initialFallbackLanguage}
                    />
                </Grid>

                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="Settings.language.localizationSource" />
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage id="Settings.language.message2" />
                    </Typography>
                </Grid>

                <Grid container direction="row" alignItems="flex-end" className={classes.languageItems}>
                    <Grid className={classes.formInput}>
                        <FormSelect
                            form={form}
                            fullWidth
                            isRequired
                            label={intl.formatMessage({ id: 'Settings.language.label2' })}
                            formItemKey="language.localizationSource"
                            initialValue={LOCALIZATION_SOURCE_OPTIONS.find(
                                (option) => option.id === get(localization, 'sourceType', '')
                            )}
                            optionList={LOCALIZATION_SOURCE_OPTIONS}
                        />
                    </Grid>

                    <Grid item>
                        <Typography variant="body1" className={classes.smallText}>
                            <FormattedMessage id="Settings.language.withPath" />
                        </Typography>
                    </Grid>

                    <Grid className={classes.formInputLarge}>
                        <FormInput
                            disabled={isLocalizationSourceSelectedValueUserProvided}
                            fullWidth
                            form={form}
                            type="text"
                            formItemKey="language.path"
                            initialValue={get(localization, 'valuePath', '')}
                            placeholder={intl.formatMessage({ id: 'Settings.language.label3' })}
                            helperText={
                                isLocalizationSourceSelectedValueCookie
                                    ? intl.formatMessage({ id: 'Settings.identity.helperText' })
                                    : ''
                            }
                        />
                    </Grid>
                </Grid>

                <Grid item>
                    <Typography
                        variant="body1"
                        className={classes.message}
                        style={{ paddingTop: '39px' }}
                    >
                        <FormattedMessage id="Settings.language.message3.1" />{' '}
                        <a target="_blank" rel="noopener noreferrer" href="https://regexone.com/">
                            regex
                        </a>{' '}
                        <FormattedMessage id="Settings.language.message3.2" />
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography>
                        <Button
                            id="yeself-button"
                            variant="contained"
                            color="primary"
                            className={classes.btn}
                            onClick={handleSaveSettings}
                        >
                            <FormattedMessage id="Buttons.apply" />
                        </Button>
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant="subtitle1" className={classes.title}>
                        <FormattedMessage id="Settings.language.translations" />
                    </Typography>
                </Grid>

                <Grid item>
                    <Typography variant="body1" className={classes.message}>
                        <FormattedMessage id="Settings.language.message4" />
                    </Typography>
                </Grid>
                <Grid container direction="row">
                    <Typography className={classes.btnImport}>
                        <Button
                            id="yeself-button"
                            variant="contained"
                            color="primary"
                            aria-owns={anchorElImport ? 'long-menu' : undefined}
                            aria-haspopup="true"
                            onClick={(event) => {
                                if (addonsWithLanguage.length === 0) {
                                    document.getElementById('hidden-clicker')?.click();
                                } else {
                                    setAnchorElImport(event.currentTarget);
                                }
                            }}
                        >
                            <FormattedMessage id="Buttons.import" />
                        </Button>

                        {addonsWithLanguage.length === 0 && (
                            <input
                                style={{ display: 'none' }}
                                type="file"
                                name="file"
                                onChange={handleImportTranslations}
                                id="hidden-clicker"
                            />
                        )}

                        <Menu
                            id="create-import-menu"
                            open={Boolean(anchorElImport)}
                            onClose={() => setAnchorElImport(null)}
                            anchorEl={anchorElImport}
                            getContentAnchorEl={null}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{
                                style: {
                                    maxHeight: ITEM_HEIGHT * 4.5,
                                },
                            }}
                        >
                            <MenuItem id="yeself-menu-item">
                                <label htmlFor="import-translation-file">
                                    <input
                                        className={classes.importTranslationsInput}
                                        type="file"
                                        name="file"
                                        onChange={handleImportTranslations}
                                        id="import-translation-file"
                                    />
                                    <FormattedMessage id="Settings.language.menu.adopt" />
                                </label>
                            </MenuItem>

                            {addonsWithLanguage.map((option) => (
                                <MenuItem id="yeself-menu-item" key={option.id}>
                                    <label htmlFor={option.id}>
                                        <input
                                            className={classes.importTranslationsInput}
                                            type="file"
                                            name="file"
                                            onChange={handleAddOnImportTranslations(option)}
                                            id={option.id}
                                        />
                                        {option.addon.vendor.name}
                                    </label>
                                </MenuItem>
                            ))}
                        </Menu>
                    </Typography>
                    <Typography>
                        <Button
                            id="yeself-button"
                            variant="contained"
                            color="primary"
                            aria-owns={anchorElExport ? 'long-menu' : undefined}
                            aria-haspopup="true"
                            onClick={(event) => {
                                setAnchorElExport(event.currentTarget);
                            }}
                        >
                            <FormattedMessage id="Buttons.export" />
                        </Button>

                        <Menu
                            id="create-export-menu"
                            open={Boolean(anchorElExport)}
                            onClose={() => setAnchorElExport(null)}
                            anchorEl={anchorElExport}
                            getContentAnchorEl={null}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{
                                style: {
                                    maxHeight: ITEM_HEIGHT * 4.5,
                                },
                            }}
                        >
                            <MenuItem id="yeself-menu-item" onClick={handleExportTranslations('csv')}>
                                <FormattedMessage id="Settings.language.menu.adopt.csv" />
                            </MenuItem>

                            <MenuItem id="yeself-menu-item" onClick={handleExportTranslations('excel')}>
                                <FormattedMessage id="Settings.language.menu.adopt.excel" />
                            </MenuItem>

                            {addonsWithLanguage.map((option) => (
                                <MenuItem
                                    id="yeself-menu-item"
                                    key={option.id}
                                    onClick={handleAddOnExportTranslations(option)}
                                >
                                    {option.addon.vendor.name}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(injectIntl(Language));
